/**
 * Get media URL with optional transformations
 * This abstraction allows us to switch providers without changing client code
 *
 * @param publicId - The media's public ID from the provider
 * @param cloudName - Your Cloudinary cloud name (required for cloudinary provider)
 * @param provider - Storage provider (default: 'cloudinary')
 * @param transform - Optional transformation parameters
 */
export const getMediaUrl = (publicId, cloudName, provider = 'cloudinary', transform) => {
    switch (provider) {
        case 'cloudinary':
            return buildCloudinaryUrl(publicId, cloudName, transform);
        case 's3':
        case 'gcs':
            // Future: implement S3/GCS URL building
            throw new Error(`Provider ${provider} not yet implemented`);
        default:
            throw new Error(`Unknown provider: ${provider}`);
    }
};
/**
 * Build Cloudinary URL with transformations
 */
const buildCloudinaryUrl = (publicId, cloudName, transform) => {
    const baseUrl = `https://res.cloudinary.com/${cloudName}/image/upload`;
    if (!transform) {
        return `${baseUrl}/${publicId}`;
    }
    const transformations = [];
    if (transform.width)
        transformations.push(`w_${transform.width}`);
    if (transform.height)
        transformations.push(`h_${transform.height}`);
    if (transform.quality)
        transformations.push(`q_${transform.quality}`);
    if (transform.format)
        transformations.push(`f_${transform.format}`);
    if (transform.crop)
        transformations.push(`c_${transform.crop}`);
    const transformStr = transformations.join(',');
    return `${baseUrl}/${transformStr}/${publicId}`;
};
/**
 * Get responsive image srcset for different screen sizes
 */
export const getResponsiveImageSrcSet = (publicId, cloudName, provider = 'cloudinary') => {
    const widths = [320, 640, 768, 1024, 1280, 1536];
    return widths
        .map((width) => {
        const url = getMediaUrl(publicId, cloudName, provider, { width, format: 'auto', quality: 80 });
        return `${url} ${width}w`;
    })
        .join(', ');
};
/**
 * Get optimized thumbnail URL
 */
export const getThumbnailUrl = (publicId, cloudName, provider = 'cloudinary', size = 200) => {
    return getMediaUrl(publicId, cloudName, provider, {
        width: size,
        height: size,
        crop: 'fill',
        format: 'auto',
        quality: 80,
    });
};
/**
 * Extract public ID from full Cloudinary URL
 * Useful for migration scenarios
 */
export const extractPublicIdFromUrl = (url) => {
    const match = url.match(/\/upload\/(?:v\d+\/)?(.+?)(?:\.[^.]+)?$/);
    return match ? match[1] : null;
};
/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0)
        return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
/**
 * Check if a media item is an image based on MIME type
 */
export const isImage = (mimeType) => {
    return mimeType.startsWith('image/');
};
/**
 * Check if a media item is a video based on MIME type
 */
export const isVideo = (mimeType) => {
    return mimeType.startsWith('video/');
};
/**
 * Get file extension from filename
 */
export const getFileExtension = (filename) => {
    return filename.split('.').pop()?.toLowerCase() ?? '';
};
/**
 * Generate a display name for media based on filename and caption
 */
export const getMediaDisplayName = (filename, caption) => {
    if (caption?.trim()) {
        return caption.trim();
    }
    // Remove file extension and clean up filename
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    return nameWithoutExt.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};
/**
 * Filter media by tags
 */
export const filterMediaByTags = (media, selectedTags) => {
    if (selectedTags.length === 0) {
        return media;
    }
    return media.filter(item => selectedTags.some(tag => item.tags.includes(tag)));
};
/**
 * Filter media by publication status
 */
export const filterMediaByPublication = (media, filter) => {
    if (filter === 'all') {
        return media;
    }
    return media.filter(item => filter === 'published' ? item.isPublished : !item.isPublished);
};
/**
 * Filter media by search query (searches filename and caption)
 */
export const filterMediaBySearch = (media, query) => {
    if (!query.trim()) {
        return media;
    }
    const searchTerm = query.toLowerCase().trim();
    return media.filter(item => {
        const filename = item.filename.toLowerCase();
        const caption = item.caption?.toLowerCase() ?? '';
        return filename.includes(searchTerm) || caption.includes(searchTerm);
    });
};
/**
 * Sort media by various criteria
 */
export const sortMedia = (media, sortBy) => {
    const sorted = [...media];
    switch (sortBy) {
        case 'newest':
            return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        case 'oldest':
            return sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        case 'name':
            return sorted.sort((a, b) => a.filename.localeCompare(b.filename));
        case 'size':
            return sorted.sort((a, b) => b.sizeBytes - a.sizeBytes);
        default:
            return sorted;
    }
};
/**
 * Get unique tags from a collection of media items
 */
export const getUniqueTags = (media) => {
    const allTags = media.flatMap(item => item.tags);
    return Array.from(new Set(allTags)).sort();
};
/**
 * Check if media is eligible for cleanup (deleted and not referenced)
 */
export const isEligibleForCleanup = (media, isReferenced) => {
    return media.status === 'deleted' && !isReferenced;
};
/**
 * Generate Cloudinary folder path based on entity type and uploader
 */
export const generateCloudinaryFolder = (entityType, uploaderId, entityId) => {
    const basePath = 'wineryxplorer';
    if (entityType === 'shared') {
        return `${basePath}/shared`;
    }
    if (entityId) {
        return `${basePath}/${entityType}s/${uploaderId}/${entityId}`;
    }
    return `${basePath}/${entityType}s/${uploaderId}`;
};
